<script setup lang="ts">
/**
 * 全局输入弹窗容器 —— 在 AppLayout 挂一次,渲染 usePrompt() 的当前请求。
 * 替代 window.prompt:视觉跟应用主题一致、Esc/遮罩取消、Enter 确认、自动聚焦输入框。
 */
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { usePrompt } from '@/composables/usePrompt'

const { state, _settle } = usePrompt()

const inputEl = ref<HTMLInputElement | null>(null)

function onConfirm() {
  _settle(state.value)
}
function onCancel() {
  _settle(null)
}

// 打开时聚焦 + 选中默认值,方便直接改写
watch(
  () => state.open,
  (open) => {
    if (open) {
      void nextTick(() => {
        inputEl.value?.focus()
        inputEl.value?.select()
      })
    }
  }
)

function onKeydown(e: KeyboardEvent) {
  if (!state.open) return
  if (e.key === 'Escape') {
    e.preventDefault()
    onCancel()
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <div v-if="state.open" class="overlay" @click.self="onCancel">
    <div class="modal" role="dialog" aria-modal="true">
      <div class="title">{{ state.title }}</div>
      <p v-if="state.message" class="message">{{ state.message }}</p>
      <input
        ref="inputEl"
        v-model="state.value"
        class="prompt-input"
        :type="state.inputType"
        :placeholder="state.placeholder"
        :step="state.inputType === 'number' ? '0.01' : undefined"
        @keydown.enter.prevent="onConfirm"
      />
      <div class="actions">
        <button type="button" class="btn-cancel" @click="onCancel">{{ state.cancelText }}</button>
        <button
          type="button"
          class="btn-confirm"
          :class="{ danger: state.danger }"
          @click="onConfirm"
        >
          {{ state.confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 高于普通 modal(100)和 toast(200):输入弹窗可能从 modal 内触发,要盖在最上 */
  z-index: 300;
  padding: 24px;
}
.modal {
  width: 400px;
  max-width: 100%;
  background: var(--color-background-primary);
  border-radius: var(--border-radius-lg);
  border: 0.5px solid var(--color-border-tertiary);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  padding: 20px;
}
.title {
  font-size: var(--fs-page);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
  margin-bottom: 8px;
}
.message {
  margin: 0 0 12px;
  font-size: var(--fs-control);
  line-height: 1.6;
  color: var(--color-text-secondary);
  white-space: pre-line;
  word-break: break-word;
}
.prompt-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  font-size: var(--fs-control);
  font-family: inherit;
  color: var(--color-text-primary);
  background: var(--color-background-secondary);
  border: var(--border-width-control) solid var(--color-border-control);
  border-radius: var(--border-radius-md);
  margin-bottom: 18px;
}
.prompt-input:focus {
  outline: none;
  border-color: var(--color-border-info, var(--color-text-info));
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.actions button {
  padding: 6px 16px;
  font-size: var(--fs-control);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-family: inherit;
  border: var(--border-width-control) solid transparent;
}
.btn-cancel {
  background: transparent;
  border-color: var(--color-border-control);
  color: var(--color-text-secondary);
}
.btn-cancel:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}
.btn-confirm {
  background: var(--color-background-info);
  color: var(--color-text-info);
}
.btn-confirm.danger {
  background: var(--color-background-danger);
  color: var(--color-text-danger);
}
.btn-confirm:hover {
  opacity: 0.85;
}
</style>
